import { useTranslation } from 'next-i18next';
import { MouseEvent, useCallback, useRef, useState } from 'react';
import { SlidersHorizontalIcon } from '@phosphor-icons/react';

import TextButton from '_components/UI/TextButton';
import { useFilters } from '@components/algolia/SearchResults/FiltersProvider';
import { FEATURE_FLAG_PLP_FILTER_PILLS } from '@config/feature-flag';
import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';

import FilterPill from './FilterPill';

import styles from './FilterBar.module.scss';

/**
 * Redesigned PLP filter bar (ED-320): the first 8 facets as dropdown pills —
 * ordered by the index's facetOrdering — followed by a "View filters" button
 * that opens the all-filters panel. At every viewport width the bar is a
 * single line that never wraps — pills that would push the "View filters"
 * button past the right edge are hidden (their facets remain reachable
 * through the all-filters panel).
 */
const FilterBar = () => {
  const { t } = useTranslation(['searchResults']);
  const { pillFacets, openFiltersPanel } = useFilters();

  if (pillFacets.length === 0) return null;

  return (
    <div className={styles.filterBar}>
      {/* "View filters" below always renders, so facets stay reachable when pills are off. */}
      {FEATURE_FLAG_PLP_FILTER_PILLS && (
        <FilterPillsRow pillFacets={pillFacets} />
      )}
      <TextButton
        type="white"
        size="small"
        label={t('search.facets.viewFilters')}
        trailingIcon={SlidersHorizontalIcon}
        trailingIconSize={20}
        trailingIconWeight="thin"
        className={styles.viewFiltersButton}
        onClick={(event: MouseEvent<HTMLButtonElement>) =>
          openFiltersPanel(event.currentTarget)
        }
      />
    </div>
  );
};

const FilterPillsRow = ({ pillFacets }: { pillFacets: string[] }) => {
  const pillsRef = useRef<HTMLDivElement>(null);
  // Start with zero visible pills so the first (pre-paint) measurement runs
  // against an un-overflowed bar. Seeding this with `pillFacets.length` would
  // render every pill in-flow before the fit calculation runs; on a mobile
  // viewport that overflow inflates the bar's measured width, so the very
  // first measurement would conclude every pill fits and never correct itself.
  const [visiblePillCount, setVisiblePillCount] = useState(0);

  const measurePillsThatFit = useCallback(() => {
    const pillsContainer = pillsRef.current;
    if (!pillsContainer) return;

    // Match pills by class, never by child position: while a pill popover is
    // open, Base UI mounts focus-guard elements as extra children of the pills
    // container, and counting those as pills corrupts the fit calculation
    // (every hidden pill un-hides and the row overflows).
    const pills = (Array.from(pillsContainer.children) as HTMLElement[]).filter(
      (child) => child.classList.contains(styles.pillTrigger)
    );
    if (pills.length === 0) return;

    const gap =
      parseFloat(window.getComputedStyle(pillsContainer).columnGap) || 0;
    const available = pillsContainer.getBoundingClientRect().width;
    let used = 0;
    let count = 0;
    for (let i = 0; i < pills.length; i += 1) {
      const pillWidth = pills[i].getBoundingClientRect().width;
      const next = count === 0 ? pillWidth : used + gap + pillWidth;
      if (next > available) break;
      used = next;
      count += 1;
    }
    setVisiblePillCount(count);
  }, []);

  // Key on the facet list's content: `pillFacets` gets a fresh array identity
  // on every InstantSearch render, which would tear down and re-create the
  // observer each time.
  const pillFacetsKey = pillFacets.join('|');

  useLayoutEffect(() => {
    measurePillsThatFit();

    let cancelled = false;
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => {
        if (!cancelled) measurePillsThatFit();
      });
    }

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measurePillsThatFit);
      return () => {
        cancelled = true;
        window.removeEventListener('resize', measurePillsThatFit);
      };
    }

    const pillsContainer = pillsRef.current;
    if (!pillsContainer)
      return () => {
        cancelled = true;
      };

    const resizeObserver = new ResizeObserver(measurePillsThatFit);
    resizeObserver.observe(pillsContainer);
    // Pill widths change when refinement counts are added to their labels;
    // observing each child re-runs the fit calculation on those changes.
    (Array.from(pillsContainer.children) as HTMLElement[]).forEach((child) =>
      resizeObserver.observe(child)
    );

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
    };
  }, [measurePillsThatFit, pillFacetsKey]);

  return (
    <div ref={pillsRef} className={styles.pills}>
      {pillFacets.map((attribute, index) => (
        <FilterPill
          key={attribute}
          attribute={attribute}
          hidden={index >= visiblePillCount}
        />
      ))}
    </div>
  );
};

export default FilterBar;
